import { DotLottieReact } from '@lottiefiles/dotlottie-react'
import { useForm, type Control, type FieldValues, type Path } from 'react-hook-form'
import { Link } from 'react-router-dom'
import { z } from 'zod'

import { env } from 'src/config/env'
import successLottie from 'src/shared/assets/lottie/success-animation.lottie'
import { ThinXIcon } from 'src/shared/components/icons/toasts/thin-x-icon'
import { Form } from 'src/shared/components/ui/forms/form'
import { InputFormField } from 'src/shared/components/ui/forms/form-fields/input'
import { WebpImage } from 'src/shared/components/ui/image/webp-image'
import { Dialog, DialogClose, DialogContent, DialogTitle } from 'src/shared/components/ui/popups/dialog'
import HouseInViewImageFallbackImage from 'src/shared/features/home/assets/lynx-intro/house-air-view.jpg'
import HouseInViewImageWebp from 'src/shared/features/home/assets/lynx-intro/house-air-view.webp'
import useBreakpoint from 'src/shared/hooks/use-breakpoint'
import { useToast } from 'src/shared/hooks/use-toast'
import { phoneSchema } from 'src/shared/schemas/forms/consumer-auth'
import { cn } from 'src/shared/utils/cn'

import StartJourneyButtonBase from '../../buttons/start-journey-button-base'
import PhoneNumberSmsConsent from '../../miscellaneous/disclaimer/phone-number-sms-consent'

interface PhoneNumberFormFieldProps<T extends FieldValues> {
  control: Control<T>
  name: Path<T>
  label: string
  placeholder?: string
  type?: 'text' | 'email' | 'password' | 'number' | 'tel' | 'currency' | 'date'
}

function PhoneNumberFormField<T extends FieldValues>({
  control,
  name,
  label,
  placeholder,
  type,
}: PhoneNumberFormFieldProps<T>) {
  return (
    <InputFormField
      control={control}
      name={name}
      label={label}
      placeholder={placeholder}
      className="space-y-1"
      fieldClassName="border-none text-[16px] focus-visible:outline-none xs:text-[16px] translate-y-[22px] placeholder:translate-y-0 bg-transparent px-5"
      floatingLabelClassName="w-fit peer-placeholder-shown:text-[16px] peer-placeholder-shown:start-1/2 peer-placeholder-shown:translate-x-[-50%] peer-focus:start-2 peer-focus:translate-x-0 peer-focus:text-primary pointer-events-none bg-transparent -translate-y-[1px] px-1 peer-focus:px-1 peer-focus:-translate-y-[-3px] peer-placeholder-shown:translate-y-[-42%]"
      fieldContainerClassName="bg-[#F8F7F5] rounded-[12px] h-[60px]"
      type={type}
      autoComplete="off"
      autoFocus={true}
    />
  )
}

const phoneFormSchema = z.object({
  phone: phoneSchema,
})

type PhoneFormData = z.infer<typeof phoneFormSchema>

function PhoneNumberDisclaimer2() {
  return (
    <p className="text-center text-[13px] font-medium leading-[1.6] text-muted">
      By clicking “Start my journey”, you accept our{' '}
      <Link to={env.LINK_TO_TERMS_OF_USE} target="_blank" className="underline">
        Terms and Conditions
      </Link>{' '}
      and{' '}
      <Link to={env.LINK_TO_PRIVACY_POLICY} target="_blank" className="underline">
        Privacy Policy
      </Link>
      .
    </p>
  )
}

interface StartJourneyPopupBaseProps {
  isOpen: boolean
  setOpen: (open: boolean) => void
  onSubmit: (phoneNumber: string) => Promise<void>
  showSuccessView?: boolean
  isSubmitting?: boolean
}

/** Base component for start journey popup.
 * Use this component as the base ui for all start journey popups.
 * Can use onSubmit props to handle form submission.
 */
export default function StartJourneyPopupBase({
  isOpen,
  setOpen,
  onSubmit,
  showSuccessView,
  isSubmitting,
}: StartJourneyPopupBaseProps) {
  const { isMobile } = useBreakpoint()
  const { toast } = useToast()

  const form = useForm<PhoneFormData>({
    defaultValues: {
      phone: '',
    },
  })

  const onSubmitHandler = async (data: PhoneFormData) => {
    // Perform phone number validation
    const parsedData = phoneSchema.safeParse(data.phone)
    if (!parsedData.success) {
      toast({
        title: 'Invalid phone number',
        description: parsedData.error.errors[0].message,
        variant: 'destructive',
      })
      return
    }

    await onSubmit(data.phone)
  }

  return (
    <Dialog open={isOpen} onOpenChange={setOpen}>
      <DialogContent
        hideClose={true}
        className={cn(
          'flex h-[100dvh] max-w-[100vw] flex-col overflow-y-auto border-none bg-white px-0 py-0 text-left xs:h-auto xs:max-h-[90vh] xs:w-[450px] xs:max-w-[90vw] xs:rounded-[40px] sm:rounded-[40px]',
        )}
      >
        {/* Header Image */}
        <div className="relative h-[200px] w-full shrink-0 overflow-hidden xs:h-[250px] xs:rounded-t-[40px]">
          <WebpImage
            src={HouseInViewImageWebp}
            fallbackSrc={HouseInViewImageFallbackImage}
            alt=""
            containerClassName="size-full"
            className="size-full object-cover"
            includeFilterGrain={true}
          />
          {/* <div className="absolute inset-0 bg-primary/20" /> */}
          {/* Close button */}
          {isMobile && (
            <DialogClose className="absolute right-4 top-4 flex size-10 items-center justify-center rounded-full bg-white/90 transition-colors hover:bg-white">
              <ThinXIcon size={12} className="text-primary" />
            </DialogClose>
          )}
        </div>

        {/* Content */}
        <div className="mb-[-50px] flex flex-1 translate-y-[-50px] flex-col items-center rounded-[40px] bg-white px-[6%] pb-[38px] pt-[40px] xs:px-[50px]">
          {showSuccessView ? (
            <div className="mb-10 flex flex-col items-center justify-center">
              <div className="relative size-[100px] shrink-0 xs:mt-3">
                <DotLottieReact src={successLottie} autoplay loop={false} segment={[0, 50]} speed={1} />
              </div>
              <DialogTitle>
                <p className="mb-2 text-center text-[26px] font-semibold leading-[1.2] tracking-[-0.32px] text-primary xs:text-[32px]">
                  Text message coming your way.
                </p>
              </DialogTitle>
            </div>
          ) : (
            <>
              {/* Title Section */}
              <div className="mb-[24px] flex flex-col items-center gap-[10px] text-center">
                <DialogTitle className="text-[28px] font-semibold leading-[1.2] tracking-[-0.32px] text-primary xs:text-[32px]">
                  Start my journey
                </DialogTitle>
                <p className="text-[15px] font-medium leading-[1.6] text-muted xs:text-[16px]">
                  Enter your phone number
                </p>
              </div>

              {/* Form */}
              <Form {...form}>
                <form
                  onSubmit={form.handleSubmit(onSubmitHandler)}
                  className="flex size-full flex-col items-center justify-between gap-[15px] xs:h-auto xs:justify-start"
                >
                  <div className="flex h-full flex-col items-center gap-[15px]">
                    {/* Phone Input */}
                    <PhoneNumberFormField
                      control={form.control}
                      name="phone"
                      label="Phone number"
                      placeholder="Phone number"
                      type="tel"
                    />

                    {/* Checkbox for SMS consent */}
                    <PhoneNumberSmsConsent />
                  </div>
                  {/* Submit Button */}
                  <StartJourneyButtonBase
                    className="mt-[10px] h-[60px] w-full xs:h-[60px]"
                    type="submit"
                    isSubmitting={isSubmitting}
                  />
                  {/* Terms and Conditions disclaimer */}
                  <PhoneNumberDisclaimer2 />
                </form>
              </Form>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
